const site = {
  name: "Solstice Diary",
  shortName: "Solstice",
  url: "https://solsticediary.com",
  email: "hello@solsticediary.com",
  waitlistEndpoint: "https://formspree.io/f/xaqanndr",
  tagline: "A private guided journal for reflection.",
  footerCopy:
    "Guided journaling for private reflection, with early access updates when it is ready.",
  themeColor: "#0c2324",
  backgroundColor: "#071819",
};

const assets = {
  brand: {
    appIcon: {
      src: "/images/brand/app_icon_fg.png",
      width: 256,
      height: 256,
    },
    ogCard: {
      src: "/images/social/searchFeature-card.jpg",
      width: 1000,
      height: 1000,
    },
  },
  screenshots: {
    today: {
      src: "/images/screenshots/optimized/today.jpg",
      width: 458,
      height: 901,
    },
    guidedJournal: {
      src: "/images/screenshots/optimized/guided-journal.jpg",
      width: 452,
      height: 897,
    },
    journal: {
      src: "/images/screenshots/optimized/journal.jpg",
      width: 458,
      height: 901,
    },
    insights: {
      src: "/images/screenshots/optimized/insights.jpg",
      width: 460,
      height: 897,
    },
    story: {
      src: "/images/screenshots/optimized/story.jpg",
      width: 456,
      height: 900,
    },
    search: {
      src: "/images/screenshots/optimized/search.jpg",
      width: 456,
      height: 903,
    },
  },
  social: {
    memoryFeature: {
      src: "/images/social/memoryFeature-card.jpg",
      width: 1000,
      height: 1000,
    },
    patternFeature: {
      src: "/images/social/patternFeature-card.jpg",
      width: 1000,
      height: 1000,
    },
    searchFeature: {
      src: "/images/social/searchFeature-card.jpg",
      width: 1000,
      height: 1000,
    },
  },
  archetypes: {
    alchemist: {
      src: "/images/archetypes/optimized/the_alchemist.jpg",
      width: 400,
      height: 400,
    },
    architect: {
      src: "/images/archetypes/optimized/the_architect.jpg",
      width: 400,
      height: 400,
    },
    catalyst: {
      src: "/images/archetypes/optimized/the_catalyst.jpg",
      width: 400,
      height: 400,
    },
    commander: {
      src: "/images/archetypes/optimized/the_commander.jpg",
      width: 400,
      height: 400,
    },
    diplomat: {
      src: "/images/archetypes/optimized/the_diplomat.jpg",
      width: 400,
      height: 400,
    },
    luminary: {
      src: "/images/archetypes/optimized/the_luminary.jpg",
      width: 400,
      height: 400,
    },
    mystic: {
      src: "/images/archetypes/optimized/the_mystic.jpg",
      width: 400,
      height: 400,
    },
    sentinel: {
      src: "/images/archetypes/optimized/the_sentinel.jpg",
      width: 400,
      height: 400,
    },
    stargazer: {
      src: "/images/archetypes/optimized/the_stargazer.jpg",
      width: 400,
      height: 400,
    },
    stoic: {
      src: "/images/archetypes/optimized/the_stoic.jpg",
      width: 400,
      height: 400,
    },
    storyweaver: {
      src: "/images/archetypes/optimized/the_storyweaver.jpg",
      width: 400,
      height: 400,
    },
    vanguard: {
      src: "/images/archetypes/optimized/the_vanguard.jpg",
      width: 400,
      height: 400,
    },
  },
};

const contentPages = [
  {
    route: "/guided-journaling",
    label: "Guided journaling",
    title: "Guided journaling that helps you return to what matters",
    description:
      "Use gentle prompts, morning and evening reflections, free writing, and saved entries in one private guided journal.",
    lastModified: "2026-08-12",
    image: assets.screenshots.guidedJournal,
    imageAlt:
      "Solstice Diary guided journal showing a reflective conversation with one prompt at a time.",
    sections: [
      {
        heading: "What is guided journaling?",
        paragraphs: [
          "Guided journaling gives you a place to write with enough structure to begin, without deciding what you should feel or what your answer should be. Solstice Diary offers prompts and reflection flows, then saves the writing as journal entries you can revisit.",
        ],
      },
      {
        heading: "Start and close the day with a simple rhythm",
        paragraphs: [
          "Morning intention and evening reflection flows help you notice what matters before the day begins and make sense of what happened before it ends.",
        ],
        bullets: [
          "Morning intentions for priorities, energy, and the day ahead",
          "Evening reflections for events, feelings, and what you want to remember",
          "Daily prompts when you want a different way into the page",
        ],
      },
      {
        heading: "Write freely when you do not need a prompt",
        paragraphs: [
          "Free writing remains available for moments when you already know what you want to say. Guided and free-form entries live together, so the journal remains one continuous record rather than a collection of separate tools.",
        ],
      },
      {
        heading: "Catch a dream before it fades",
        paragraphs: [
          "Dream journaling gives you a dedicated place to record remembered scenes, feelings, images, and fragments. Solstice can guide the entry without presenting an interpretation as fact or deciding what a dream means for you.",
        ],
      },
      {
        heading: "Keep entries you can return to",
        paragraphs: [
          "Saved entries can be revisited alongside their date, mood, goals, and other context. Solstice is designed to help reflection accumulate into a useful personal record over time.",
        ],
      },
    ],
    relatedRoutes: ["/mood-tracking", "/journal-memory", "/story"],
  },
  {
    route: "/mood-tracking",
    label: "Mood tracking",
    title: "Mood tracking with the context of your real days",
    description:
      "Record mood check-ins with journal and calendar context, then notice recurring patterns without clinical claims or diagnoses.",
    lastModified: "2026-08-12",
    image: assets.screenshots.insights,
    imageAlt:
      "Solstice Diary insights screen showing mood patterns and key themes for the month.",
    sections: [
      {
        heading: "A mood check-in is a starting point",
        paragraphs: [
          "A quick mood check-in records how a day felt. Solstice connects that check-in with journal entries, goals, and life events so it is not reduced to a score without context.",
        ],
      },
      {
        heading: "See moods alongside your calendar",
        paragraphs: [
          "Calendar views help you look back across days and weeks. You can compare changes in mood with what you wrote and what was happening at the time.",
        ],
      },
      {
        heading: "Notice patterns without treating them as diagnoses",
        paragraphs: [
          "Weekly and longer-term views can make recurring moods and themes easier to notice. These patterns are reflective information, not medical conclusions, predictions, or professional advice.",
        ],
      },
      {
        heading: "Use what you notice as a prompt",
        paragraphs: [
          "A pattern can become a question for your next entry: what supported you, what drained you, or what changed? You decide what the information means and what to do with it.",
        ],
      },
    ],
    relatedRoutes: ["/guided-journaling", "/journal-memory", "/faq"],
  },
  {
    route: "/journal-memory",
    label: "Journal memory",
    title: "Find memories and connections across your journal",
    description:
      "Resurface earlier entries and search your journal by people, feelings, meaning, and time to reconnect with what mattered.",
    lastModified: "2026-08-12",
    image: assets.screenshots.search,
    imageAlt:
      "Solstice Diary search screen showing journal results and filters for date, people, emotions, and topics.",
    sections: [
      {
        heading: "A journal becomes more useful when it is easier to revisit",
        paragraphs: [
          "Writing captures a moment, but memory makes it part of a longer story. Solstice can bring earlier entries back into view when they relate to what you are reflecting on now.",
        ],
      },
      {
        heading: "Search by more than exact words",
        paragraphs: [
          "Journal search is designed to help you look for an idea even when you do not remember the exact wording. Dates, people, feelings, and topics can narrow the results.",
        ],
      },
      {
        heading: "Return to people, feelings, and periods of your life",
        paragraphs: [
          "You might look for entries about a relationship, a recurring feeling, a project, or a season of change. Search results remain a path back to your own writing rather than a replacement for it.",
        ],
      },
      {
        heading: "Memory resurfacing can be imperfect",
        paragraphs: [
          "Semantic search and AI-assisted memory features can miss relevant entries or surface a connection that does not feel meaningful. Review the original entry and use your own judgment when interpreting a result.",
        ],
      },
    ],
    relatedRoutes: [
      "/guided-journaling",
      "/ai-assisted-reflection",
      "/privacy-and-security",
    ],
  },
  {
    route: "/ai-assisted-reflection",
    label: "AI-assisted reflection",
    title: "AI-assisted reflection that stays grounded in your journal",
    description:
      "Learn how Solstice uses AI for prompts, summaries, memories, search, and insights—and where those features have limits.",
    lastModified: "2026-08-13",
    image: assets.screenshots.story,
    imageAlt:
      "Solstice Diary story screen showing reflective passages created from journal entries.",
    sections: [
      {
        heading: "AI supports the reflection; it does not replace it",
        paragraphs: [
          "Solstice uses AI to help you begin writing, organize what you have written, and notice possible connections. Your entries remain the source material, and you remain responsible for deciding what feels accurate or useful.",
        ],
      },
      {
        heading: "What AI-assisted features can do",
        bullets: [
          "Offer guided prompts and follow-up questions",
          "Create summaries and reflective views from journal content",
          "Support semantic journal search and memory resurfacing",
          "Highlight recurring moods, people, topics, and themes",
        ],
      },
      {
        heading: "AI output can be wrong or incomplete",
        paragraphs: [
          "Generated text may misunderstand context, omit something important, or make an inaccurate connection. It should not be treated as fact, diagnosis, professional advice, or a substitute for your own judgment.",
        ],
      },
      {
        heading: "Relevant content is processed on the server",
        paragraphs: [
          "When you request an AI-assisted feature, Solstice sends the needed journal content and context through its backend for processing. OpenAI and Anthropic support AI text generation, OpenAI also supports safety moderation, and Cohere creates semantic embeddings for search and retrieval. Your raw Firebase user ID or email is not used as your AI-provider identity, although the writing itself may contain details that identify you. The Privacy Policy remains the canonical explanation of data handling.",
        ],
      },
    ],
    relatedRoutes: ["/privacy-and-security", "/journal-memory", "/faq"],
  },
  {
    route: "/story",
    label: "Story",
    title: "See your journal become the story of your life",
    description:
      "Story gathers journal entries into weekly passages, monthly paragraphs, and yearly chapters you can read as your life unfolds.",
    lastModified: "2026-08-12",
    image: assets.screenshots.story,
    imageAlt:
      "Solstice Diary Story screen showing passages, paragraphs, and chapters created from journal entries.",
    video: {
      src: "/videos/story.mp4",
      poster: assets.screenshots.story.src,
      label:
        "A 22-second screen recording paging through the Solstice Diary Story book.",
    },
    sections: [
      {
        heading: "A life book that grows with your journal",
        paragraphs: [
          "Story turns regular reflection into something you can read over a longer horizon. It is designed to preserve the shape of your experience without asking you to manually assemble a retrospective.",
        ],
      },
      {
        heading: "Passages, paragraphs, and chapters",
        paragraphs: [
          "Weekly entries can become passages, monthly reflections can become paragraphs, and yearly arcs can become chapters. Together they form a book-like view of what happened, what mattered, and what changed.",
        ],
      },
      {
        heading: "Read across time",
        paragraphs: [
          "Move through the Story book to revisit earlier periods and see how smaller moments connect. The more consistently you journal, the more source material Story has to work with.",
        ],
      },
      {
        heading: "Your entries remain the source",
        paragraphs: [
          "Story is AI-assisted and may summarize something imperfectly or miss context. It should be read as a reflective interpretation of your entries, not an authoritative account, and you can return to the underlying journal record when details matter.",
        ],
      },
    ],
    relatedRoutes: [
      "/guided-journaling",
      "/journal-memory",
      "/ai-assisted-reflection",
    ],
  },
  {
    route: "/privacy-and-security",
    label: "Privacy and security",
    title: "How privacy and security work in Solstice Diary",
    description:
      "Understand how Solstice Diary protects stored writing, processes requested AI features, separates provider requests from direct account identity, and supports deletion.",
    lastModified: "2026-08-13",
    image: assets.screenshots.journal,
    imageAlt:
      "Solstice Diary journal screen showing saved entries, moods, and calendar context.",
    sections: [
      {
        heading: "Journal content is designed to be private",
        paragraphs: [
          "Solstice is built for personal reflection rather than social sharing. Much of your saved journal content is encrypted on your device before it is stored in Firestore.",
        ],
      },
      {
        heading: "Cloud storage supports account-based access",
        paragraphs: [
          "Encrypted and related account data is stored in cloud services so the app can provide its features. Access controls and other safeguards are used to protect that data, but no storage or transmission method can guarantee absolute security.",
        ],
      },
      {
        heading: "How requested AI features process your writing",
        paragraphs: [
          "AI summaries, memories, search, Story, and insights work by decrypting relevant content on Solstice Diary servers and sending the needed context to OpenAI, Anthropic, or Cohere for the applicable feature. Your raw Firebase user ID or email is not used as your identity with those AI providers, although the writing itself may contain details that identify you.",
          "This controlled server-side processing is why these requested features can work across your journal. It also means the service is not end-to-end encrypted. Our long-term direction is to reduce dependence on general-purpose AI providers as privacy-preserving technology and the product mature.",
        ],
      },
      {
        heading: "You can request deletion",
        paragraphs: [
          "The app supports deleting individual content and requesting deletion of specific data or an account. The Account and Data Deletion page explains the available routes, verification steps, Solstice-controlled active records, provider-held records, subscriptions, backups, and device-local data.",
        ],
      },
      {
        heading: "Legal pages are the canonical source",
        paragraphs: [
          "This page is a plain-language product overview. Read the Privacy Policy and Account and Data Deletion page for the full current wording about collection, processing, retention, service providers, and your choices.",
        ],
      },
    ],
    relatedRoutes: [
      "/privacy-policy",
      "/account-deletion",
      "/ai-assisted-reflection",
    ],
  },
  {
    route: "/faq",
    label: "Frequently asked questions",
    title: "Questions about Solstice Diary",
    description:
      "Clear answers about guided journaling, privacy, AI, early access, platforms, account deletion, and the distinction from therapy.",
    lastModified: "2026-08-13",
    image: assets.screenshots.today,
    imageAlt:
      "Solstice Diary Today screen showing a mood check-in, goals, and a morning intention.",
    faqs: [
      {
        question: "What is Solstice Diary?",
        answer:
          "Solstice Diary is a private guided journal for prompts, free writing, mood check-ins, goals, memories, search, and AI-assisted reflection over time.",
      },
      {
        question: "Is Solstice Diary private?",
        answer:
          "Solstice is designed for private reflection, and much of your saved journal content is encrypted on your device before cloud storage. Requested AI features use controlled server-side decryption and processing through OpenAI, Anthropic, or Cohere, without using your raw account ID or email as your AI-provider identity. Solstice also uses limited product analytics and crash diagnostics that are designed not to contain journal text. The privacy page explains these boundaries in detail.",
      },
      {
        question: "How does Solstice Diary use AI?",
        answer:
          "AI supports guided prompts, summaries, journal search, memory resurfacing, stories, and insights. Generated output can be inaccurate or incomplete, so it should be reviewed rather than treated as fact or professional advice.",
      },
      {
        question: "Which platforms does Solstice Diary support?",
        answer:
          "Solstice Diary is being developed for Android and iOS. There is not yet a public app-store download link.",
      },
      {
        question: "How can I get access?",
        answer:
          "Join the website waitlist with your email. The form is processed by Formspree, and Solstice uses the submission for the early-access and launch updates you requested. The Privacy Policy explains the related technical and retention details.",
      },
      {
        question: "Can I delete my entries or account?",
        answer:
          "Yes. You can delete individual content in the app and request deletion of specific data or your account. App-store subscriptions must be cancelled separately, and some provider, transaction, diagnostics, backup, aggregate, waitlist, or device-local records follow separate processes. See the Account and Data Deletion page for details.",
      },
      {
        question: "Is Solstice Diary therapy or a mental-health service?",
        answer:
          "No. Solstice Diary is a journaling and reflection product, not therapy, medical care, crisis support, diagnosis, or a substitute for a qualified professional.",
      },
    ],
    sections: [],
    relatedRoutes: ["/guided-journaling", "/privacy-and-security", "/about"],
  },
  {
    route: "/about",
    label: "About Solstice Diary",
    title: "A private guided journal built for a longer view",
    description:
      "Learn about the Solstice Diary mission, product principles, current early-access status, and how to contact the brand.",
    lastModified: "2026-08-13",
    image: assets.brand.appIcon,
    imageAlt: "The Solstice Diary sunburst app mark.",
    sections: [
      {
        heading: "Our mission",
        paragraphs: [
          "Solstice Diary began with a simple need: a space designed to feel safe and private while you talk through thoughts and preserve memories, without turning them into content for a public feed or attaching a raw account identity to requests sent to an AI provider.",
          "Our mission is to build a trusted reflection space where people can think honestly, understand their patterns, and keep a meaningful record of their lives. Today, AI-assisted requests are routed through Solstice to OpenAI, Anthropic, or Cohere for the applicable feature without using a raw account ID or email as your provider identity. Our long-term direction is to move these capabilities further away from general-purpose AI systems as privacy-preserving technology and the product mature.",
        ],
      },
      {
        heading: "Product principles",
        bullets: [
          "Private reflection comes before social performance",
          "Guidance should make writing easier without deciding what an entry means",
          "AI should support human judgment and clearly acknowledge its limits",
          "Past writing should remain useful and easier to revisit over time",
          "Public explanations should match how the product actually works",
        ],
      },
      {
        heading: "Reflection support, not therapy",
        paragraphs: [
          "Solstice aims to offer a warm, thoughtful conversation that helps you put experience into words and notice what matters. It is a journaling and reflection product, not a therapist, diagnosis, medical care, crisis service, or substitute for a qualified professional.",
        ],
      },
      {
        heading: "Current status",
        paragraphs: [
          "Solstice Diary is in early access, and current tester access may remain free while the experience is refined. The app also supports paid subscriptions when offered, using localized prices displayed by Apple or Google before purchase. Android and iOS are supported product platforms, but public store listings are not available yet. The website waitlist is the public route to access updates.",
        ],
      },
      {
        heading: "Contact Solstice Diary",
        paragraphs: [
          "Questions about the product, privacy, or access can be sent to hello@solsticediary.com.",
        ],
      },
    ],
    relatedRoutes: ["/guided-journaling", "/faq", "/privacy-and-security"],
    visualStyle: "brand",
  },
];

const contentPageByRoute = Object.fromEntries(
  contentPages.map((page) => [page.route, page]),
);

const navLinks = [
  { href: "/guided-journaling", label: "Journal" },
  { href: "/journal-memory", label: "Memory" },
  { href: "/story", label: "Your Story" },
  { href: "/privacy-and-security", label: "Privacy" },
  { href: "/faq", label: "FAQ" },
  { href: "/about", label: "About" },
];

const footerLinkGroups = [
  {
    label: "Product",
    links: contentPages
      .filter(({ route }) => !["/faq", "/about"].includes(route))
      .map(({ route, label }) => ({ href: route, label })),
  },
  {
    label: "Company",
    links: [
      { href: "/faq", label: "FAQ" },
      { href: "/about", label: "About" },
      { href: "/terms", label: "Terms of Service" },
      { href: "/privacy-policy", label: "Privacy Policy" },
      { href: "/account-deletion", label: "Account Deletion" },
    ],
  },
];

const rotatingSlides = [
  {
    key: "today",
    label: "Today",
    eyebrow: "Daily rhythm",
    title: "Start where you are today.",
    description:
      "Check in with your mood, revisit your goals, and choose a prompt that fits the shape of your day.",
    image: assets.screenshots.today,
    alt: "Solstice Diary Today screen showing a mood check-in, goals, and morning intention.",
  },
  {
    key: "guided",
    label: "Guided Journal",
    eyebrow: "Guided reflection",
    title: "Write with gentle guidance.",
    description:
      "Solstice helps you reflect one prompt at a time, then turns the conversation into a journal entry you can revisit.",
    image: assets.screenshots.guidedJournal,
    alt: "Solstice Diary guided journal screen showing a reflective conversation with one prompt at a time.",
  },
  {
    key: "journal",
    label: "Journal",
    eyebrow: "Your record",
    title: "Keep your days connected.",
    description:
      "Entries, moods, goals, and life events sit together, so your journal feels like a living record of your story.",
    image: assets.screenshots.journal,
    alt: "Solstice Diary journal screen showing a calendar with moods, events, and highlighted dates.",
  },
  {
    key: "insights",
    label: "Insights",
    eyebrow: "Patterns over time",
    title: "Notice what keeps returning.",
    description:
      "See recurring moods, people, topics, and themes so your reflection can become clearer over time.",
    image: assets.screenshots.insights,
    alt: "Solstice Diary insights screen showing mood patterns and key themes for the month.",
  },
  {
    key: "story",
    label: "Story",
    eyebrow: "Your life in chapters",
    title: "See your life take shape over time.",
    description:
      "Solstice can gather your weeks, months, and years into reflective chapters that help you remember where you have been and what has been changing.",
    image: assets.screenshots.story,
    alt: "Solstice Diary story screen showing generated passages, paragraphs, and chapters from journal entries.",
  },
];

const featureColumns = [
  [
    {
      key: "memory",
      size: "large",
      eyebrow: "Memory recall",
      description:
        "Past journal entries resurface when today echoes something familiar.",
      image: assets.social.memoryFeature,
      alt: "Solstice Diary memory feature showing a recent journal entry linked to an earlier note about sleep.",
    },
    {
      key: "privacy",
      size: "small",
      emoji: "\uD83D\uDD12",
      eyebrow: "Private by design",
      description:
        "Write in a guided journaling app built for privacy, reflection, and long-term trust.",
    },
  ],
  [
    {
      key: "context",
      size: "small",
      emoji: "\uD83D\uDC9B",
      eyebrow: "Emotional context",
      description:
        "Feelings, entries, and daily context stay connected so guided reflection becomes more useful over time.",
    },
    {
      key: "patterns",
      size: "large",
      eyebrow: "Mood tracking",
      description:
        "Use mood tracking and weekly overviews to see what keeps returning in your journal so you can understand patterns and make gentler changes.",
      image: assets.social.patternFeature,
      alt: "Solstice Diary mood tracking overview screen showing mood check-ins and entry emotions.",
    },
  ],
  [
    {
      key: "search",
      size: "large",
      eyebrow: "Search your story",
      description:
        "Search your journal by memory, person, feeling, or time in your life to quickly find what mattered.",
      image: assets.social.searchFeature,
      alt: "Solstice Diary search screen showing filters for date, people, and emotions.",
    },
    {
      key: "continuity",
      size: "small",
      emoji: "\uD83C\uDF3F",
      eyebrow: "One quiet flow",
      description:
        "Move from morning intentions, dream journaling, and guided prompts to lasting entries and AI-assisted insight in one calm flow.",
    },
  ],
];

const valueCards = [
  {
    title: "A guided journal, not just a chat",
    text: "Gentle conversations become lasting entries you can revisit like pages in your own story.",
  },
  {
    title: "Patterns you can actually revisit",
    text: "Moods, memories, and recurring themes stay connected over time, revealing a clearer picture of your life.",
  },
  {
    title: "Private by design",
    text: "Much of your saved writing is encrypted on your device before cloud storage, with no social pressure to share or perform.",
  },
];

const testimonialCards = [
  {
    quote:
      "Solstice helps me slow down at the end of the day and be a little more honest with myself. I do not feel like I have to have the right words. It just helps me reflect more clearly.",
    name: "Early user",
    note: "Reflection and clarity",
  },
  {
    quote:
      "I started noticing patterns in my moods and the kinds of days I was having, which was something I usually missed in the moment. That alone made journaling feel much more useful.",
    name: "Early user",
    note: "Patterns over time",
  },
  {
    quote:
      "What makes Solstice different for me is that it feels private in the right way. It feels safe enough to be honest, which makes it much easier to return to.",
    name: "Early user",
    note: "Privacy and honesty",
  },
];

const archetypes = [
  {
    key: "alchemist",
    name: "The Alchemist",
    image: assets.archetypes.alchemist,
  },
  {
    key: "architect",
    name: "The Architect",
    image: assets.archetypes.architect,
  },
  {
    key: "catalyst",
    name: "The Catalyst",
    image: assets.archetypes.catalyst,
  },
  {
    key: "commander",
    name: "The Commander",
    image: assets.archetypes.commander,
  },
  {
    key: "diplomat",
    name: "The Diplomat",
    image: assets.archetypes.diplomat,
  },
  {
    key: "luminary",
    name: "The Luminary",
    image: assets.archetypes.luminary,
  },
  {
    key: "mystic",
    name: "The Mystic",
    image: assets.archetypes.mystic,
  },
  {
    key: "sentinel",
    name: "The Sentinel",
    image: assets.archetypes.sentinel,
  },
  {
    key: "stargazer",
    name: "The Stargazer",
    image: assets.archetypes.stargazer,
  },
  {
    key: "stoic",
    name: "The Stoic",
    image: assets.archetypes.stoic,
  },
  {
    key: "storyweaver",
    name: "The Storyweaver",
    image: assets.archetypes.storyweaver,
  },
  {
    key: "vanguard",
    name: "The Vanguard",
    image: assets.archetypes.vanguard,
  },
];

const privacyNotes = [
  "Guided journaling",
  "Encrypted before cloud storage",
  "Private by design",
];

const freeAccessNotes = [
  "No payment required",
  "All current features included",
  "Early access",
];

const homePage = {
  route: "/",
  sectionLabel: "A private guided journal",
  hero: {
    title: "A private guided journal for reflection, memory, and patterns.",
    lead: "Solstice Diary combines gentle prompts, free writing, mood context, journal search, and AI-assisted reflection so you can understand your days over time.",
    primaryCtaLabel: "Join Early Access",
    primaryCtaHref: "#waitlist",
    secondaryCtaLabel: "Explore Features",
    secondaryCtaHref: "#features",
    meta: "Guided journaling. Mood tracking. Private AI-assisted reflection.",
  },
  featuresSection: {
    label: "Explore Solstice",
    title: "Understand how each part of your journal works.",
    description:
      "Start with a clear answer, then explore the details behind guided writing, mood context, memory, AI assistance, and privacy.",
  },
  archetypesSection: {
    label: "Archetypes",
    title: "Choose the tone that meets you best.",
    description:
      "Solstice can shift its guidance voice through archetypes, so your reflection feels more natural and personal.",
  },
  privacySection: {
    label: "Private by design",
    title: "Private journaling, designed to feel safe enough for honesty.",
    description:
      "Solstice is built for private reflection first. Saved writing is encrypted before cloud storage, while requested AI features use controlled server-side processing explained in the Privacy Policy.",
  },
  testimonialsSection: {
    label: "Testimonials",
    title:
      "Early reflections from people using Solstice for guided journaling.",
    description:
      "A slower evening, clearer patterns, and a more private place to be honest with yourself.",
  },
  availabilitySection: {
    label: "Early access",
    title: "Free while Solstice is taking shape.",
    description:
      "Early access is free while the app is still being refined. You can explore the full experience now, and pricing will be introduced more clearly before launch.",
  },
  ctaSection: {
    label: "Early access",
    title: "Get early access to guided journaling with Solstice.",
    description:
      "Leave your email and we will reach out when the app opens, with early access and launch updates.",
  },
};

const homePageLinks = contentPages
  .filter(({ route }) => !["/faq", "/about"].includes(route))
  .map((page) => ({
    href: page.route,
    label: page.label,
    title: page.title,
    description: page.description,
  }));

const notFoundPage = {
  title: "This page could not be found.",
  description:
    "The page may have moved, the link may be outdated, or the address may be incorrect.",
  primaryLinkLabel: "Back to home",
  primaryLinkHref: "/",
  secondaryLinkLabel: "Read the privacy policy",
  secondaryLinkHref: "/privacy-policy",
};

const organizationId = `${site.url}/#organization`;
const websiteId = `${site.url}/#website`;
const applicationId = `${site.url}/#mobile-application`;

function canonicalUrl(route) {
  return route === "/" ? `${site.url}/` : `${site.url}${route}`;
}

function makeBreadcrumb(route, label) {
  const url = canonicalUrl(route);
  return {
    "@type": "BreadcrumbList",
    "@id": `${url}#breadcrumb`,
    itemListElement: [
      {
        "@type": "ListItem",
        position: 1,
        name: "Home",
        item: `${site.url}/`,
      },
      {
        "@type": "ListItem",
        position: 2,
        name: label,
        item: url,
      },
    ],
  };
}

function makeWebPageStructuredData(page) {
  const url = canonicalUrl(page.route);
  const pageEntity = {
    "@type": page.route === "/about" ? "AboutPage" : "WebPage",
    "@id": `${url}#webpage`,
    url,
    name: page.title,
    description: page.description,
    dateModified: page.lastModified,
    isPartOf: { "@id": websiteId },
    about: { "@id": applicationId },
    breadcrumb: { "@id": `${url}#breadcrumb` },
    primaryImageOfPage: {
      "@type": "ImageObject",
      url: `${site.url}${page.image.src}`,
      caption: page.imageAlt,
    },
  };
  const graph = [pageEntity, makeBreadcrumb(page.route, page.label)];

  if (page.faqs) {
    const faqEntity = {
      "@type": "FAQPage",
      "@id": `${url}#faq`,
      url,
      name: page.title,
      mainEntity: page.faqs.map((faq) => ({
        "@type": "Question",
        name: faq.question,
        acceptedAnswer: {
          "@type": "Answer",
          text: faq.answer,
        },
      })),
    };
    pageEntity.mainEntity = { "@id": faqEntity["@id"] };
    graph.push(faqEntity);
  }

  return { "@context": "https://schema.org", "@graph": graph };
}

contentPages.forEach((page) => {
  page.canonicalUrl = canonicalUrl(page.route);
  page.socialMetadata = {
    title: `${page.title} | Solstice Diary`,
    description: page.description,
    ogType: page.route === "/about" ? "website" : "article",
    image: `${site.url}${assets.brand.ogCard.src}`,
    imageAlt: page.imageAlt,
  };
  page.structuredData = makeWebPageStructuredData(page);
});

function makeSeo({
  route,
  title,
  description,
  structuredData,
  ogType = "website",
  imageAlt = "Solstice Diary private guided journal",
}) {
  return {
    title,
    metaDescription: description,
    canonicalUrl: canonicalUrl(route),
    ogTitle: title,
    ogDescription: description,
    ogType,
    ogImage: `${site.url}${assets.brand.ogCard.src}`,
    ogImageAlt: imageAlt,
    twitterCard: "summary_large_image",
    robots: "index,follow",
    structuredData,
  };
}

const homeStructuredData = {
  "@context": "https://schema.org",
  "@graph": [
    {
      "@type": "Organization",
      "@id": organizationId,
      name: site.name,
      url: `${site.url}/`,
      logo: {
        "@type": "ImageObject",
        url: `${site.url}${assets.brand.appIcon.src}`,
        width: assets.brand.appIcon.width,
        height: assets.brand.appIcon.height,
      },
      email: site.email,
      description:
        "Solstice Diary is a private guided journal for reflection, memory, mood context, and AI-assisted insights.",
    },
    {
      "@type": "WebSite",
      "@id": websiteId,
      url: `${site.url}/`,
      name: site.name,
      publisher: { "@id": organizationId },
    },
    {
      "@type": "MobileApplication",
      "@id": applicationId,
      name: site.name,
      description:
        "A private guided journal for prompts, free writing, mood context, memories, search, and AI-assisted reflection.",
      applicationCategory: "LifestyleApplication",
      operatingSystem: "Android, iOS",
      publisher: { "@id": organizationId },
      offers: {
        "@type": "Offer",
        price: 0,
        availability: "https://schema.org/LimitedAvailability",
        description: "Free during the current early-access period.",
      },
    },
    {
      "@type": "WebPage",
      "@id": `${site.url}/#webpage`,
      url: `${site.url}/`,
      name: "Private Guided Journal | Solstice Diary",
      description:
        "Solstice Diary is a private guided journal for reflection, memory, mood context, and AI-assisted insights over time.",
      dateModified: "2026-08-13",
      isPartOf: { "@id": websiteId },
      about: { "@id": applicationId },
    },
  ],
};

const seoByPath = {
  "/": makeSeo({
    route: "/",
    title: "Private Guided Journal | Solstice Diary",
    description:
      "Solstice Diary is a private guided journal for prompts, mood context, memory, search, and AI-assisted reflection over time.",
    structuredData: homeStructuredData,
    imageAlt: "Solstice Diary private guided journal preview",
  }),
  ...Object.fromEntries(
    contentPages.map((page) => [
      page.route,
      makeSeo({
        route: page.route,
        title: page.socialMetadata.title,
        description: page.socialMetadata.description,
        structuredData: page.structuredData,
        ogType: page.socialMetadata.ogType,
        imageAlt: page.socialMetadata.imageAlt,
      }),
    ]),
  ),
  "/terms": makeSeo({
    route: "/terms",
    title: "Terms of Service | Solstice Diary",
    description:
      "Read the Terms of Service for Solstice Diary, the private guided journal for reflection and AI-assisted features.",
    structuredData: {
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "WebPage",
          "@id": `${site.url}/terms#webpage`,
          url: `${site.url}/terms`,
          name: "Terms of Service | Solstice Diary",
          isPartOf: { "@id": websiteId },
          breadcrumb: { "@id": `${site.url}/terms#breadcrumb` },
        },
        makeBreadcrumb("/terms", "Terms of Service"),
      ],
    },
  }),
  "/privacy-policy": makeSeo({
    route: "/privacy-policy",
    title: "Privacy Policy | Solstice Diary",
    description:
      "Read how Solstice Diary handles personal information, journal content, cloud storage, and AI-assisted processing.",
    structuredData: {
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "WebPage",
          "@id": `${site.url}/privacy-policy#webpage`,
          url: `${site.url}/privacy-policy`,
          name: "Privacy Policy | Solstice Diary",
          isPartOf: { "@id": websiteId },
          breadcrumb: { "@id": `${site.url}/privacy-policy#breadcrumb` },
        },
        makeBreadcrumb("/privacy-policy", "Privacy Policy"),
      ],
    },
  }),
  "/account-deletion": makeSeo({
    route: "/account-deletion",
    title: "Account and Data Deletion | Solstice Diary",
    description:
      "Learn how to delete Solstice Diary content or request deletion of specific data, an account, and associated active-system records.",
    structuredData: {
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "WebPage",
          "@id": `${site.url}/account-deletion#webpage`,
          url: `${site.url}/account-deletion`,
          name: "Account and Data Deletion | Solstice Diary",
          isPartOf: { "@id": websiteId },
          breadcrumb: { "@id": `${site.url}/account-deletion#breadcrumb` },
        },
        makeBreadcrumb("/account-deletion", "Account and Data Deletion"),
      ],
    },
  }),
  404: {
    title: "Page Not Found | Solstice Diary",
    metaDescription:
      "The page you requested could not be found on Solstice Diary.",
    ogTitle: "Page Not Found | Solstice Diary",
    ogDescription:
      "The page you requested could not be found on Solstice Diary.",
    ogType: "website",
    ogImage: `${site.url}${assets.brand.ogCard.src}`,
    ogImageAlt: "Solstice Diary",
    twitterCard: "summary_large_image",
    robots: "noindex,nofollow",
  },
};

const legalLastModified = {
  "/terms": "2026-08-13",
  "/privacy-policy": "2026-08-13",
  "/account-deletion": "2026-08-13",
};

const sitemapEntries = [
  { route: "/", lastModified: "2026-08-13" },
  ...contentPages.map(({ route, lastModified }) => ({ route, lastModified })),
  ...Object.entries(legalLastModified).map(([route, lastModified]) => ({
    route,
    lastModified,
  })),
];

const knownRoutes = sitemapEntries.map(({ route }) => route);

module.exports = {
  assets,
  contentPageByRoute,
  contentPages,
  footerLinkGroups,
  freeAccessNotes,
  homePage,
  homePageLinks,
  knownRoutes,
  navLinks,
  notFoundPage,
  privacyNotes,
  rotatingSlides,
  seoByPath,
  sitemapEntries,
  site,
  testimonialCards,
  valueCards,
  featureColumns,
  archetypes,
};
